<template>
  <aside class="metadata-drawer" aria-label="Indicator metadata drawer">
    <header class="metadata-drawer__header">
      <div>
        <p class="metadata-drawer__eyebrow">Metadata</p>
        <h2>{{ indicatorTitle }}</h2>
      </div>
      <button
        type="button"
        class="metadata-drawer__close"
        aria-label="Close metadata drawer"
        @click="setActiveComponent('main')"
      >
        <b-icon icon="x" aria-hidden="true"></b-icon>
      </button>
    </header>

    <div class="metadata-drawer__body">
      <div v-if="loading" class="metadata-drawer__status">
        <b-spinner small variant="success" label="Loading metadata"></b-spinner>
        <span>Loading metadata</span>
      </div>

      <div v-else-if="error" class="metadata-drawer__notice">
        {{ error }}
      </div>

      <template v-else>
        <section class="metadata-section">
          <div class="metadata-section__title">
            <b-icon icon="activity" aria-hidden="true"></b-icon>
            <span>Indicator</span>
          </div>
          <dl class="metadata-list">
            <template v-for="item in indicatorRows">
              <dt :key="`${item.label}-label`">{{ item.label }}</dt>
              <dd :key="`${item.label}-value`">{{ item.value }}</dd>
            </template>
          </dl>
        </section>

        <section class="metadata-section">
          <div class="metadata-section__title">
            <b-icon icon="database-fill" aria-hidden="true"></b-icon>
            <span>Datasource</span>
          </div>
          <dl class="metadata-list">
            <template v-for="item in datasourceRows">
              <dt :key="`${item.label}-label`">{{ item.label }}</dt>
              <dd :key="`${item.label}-value`">{{ item.value }}</dd>
            </template>
          </dl>
        </section>

        <section class="metadata-section">
          <div class="metadata-section__title">
            <b-icon icon="calendar2-week" aria-hidden="true"></b-icon>
            <span>Coverage</span>
          </div>
          <div class="metadata-pills" v-if="availableYears.length">
            <span v-for="year in availableYears" :key="year" class="metadata-pill">
              {{ year }}
            </span>
          </div>
          <p v-else class="metadata-empty">No available year metadata found.</p>
        </section>

        <section class="metadata-section">
          <div class="metadata-section__title">
            <b-icon icon="collection" aria-hidden="true"></b-icon>
            <span>Available Sources</span>
          </div>
          <ul v-if="visibleIndicatorDatasources.length" class="metadata-source-list">
            <li v-for="source in visibleIndicatorDatasources" :key="source.id || source.datasource">
              {{ source.datasource || source.name || source.short_name || source.id }}
            </li>
          </ul>
          <p v-else class="metadata-empty">No datasource mapping found for this indicator.</p>
        </section>

        <section v-if="visibleTags.length" class="metadata-section">
          <div class="metadata-section__title">
            <b-icon icon="tags-fill" aria-hidden="true"></b-icon>
            <span>Tags</span>
          </div>
          <div class="metadata-pills">
            <span v-for="tag in visibleTags" :key="tag.id || tag.name" class="metadata-pill">
              {{ tag.name || tag.title || tag.label }}
            </span>
          </div>
        </section>
      </template>
    </div>
  </aside>
</template>

<script>
import ApiServices from '@/modules/data-layer/services/ApiServices';

const EMPTY_VALUE = 'Not available';

export default {
  name: 'MetadataDrawerPlugin',
  data() {
    return {
      loading: false,
      error: '',
      indicatorDetails: null,
      datasourceDetails: null,
      availableYears: [],
      indicatorDatasources: [],
      tags: [],
    };
  },
  computed: {
    selectedConfig() {
      return this.$store?.state?.MSDAT_STORE?.selectedConfigurations || {};
    },
    dataLayerState() {
      return this.$store?.state?.DL || {};
    },
    selectedIndicator() {
      const selected = this.selectedConfig.indicator;
      if (selected) return selected;

      const localId = Number(localStorage.getItem('indicatorID'));
      if (localId) {
        return this.findById(this.dataLayerState.indicators, localId);
      }

      const available = this.dataLayerState.availableDashboardIndicator || [];
      const firstId = available[0];
      return this.findById(this.dataLayerState.indicators, firstId) || null;
    },
    selectedDatasource() {
      const selected = this.selectedConfig.dataSource;
      if (selected) return selected;

      const dashboardSources = this.dataLayerState.dashboardDataSource || [];
      return dashboardSources[0] || null;
    },
    selectedPeriod() {
      return this.selectedConfig.period || this.$store?.state?.MSDAT_STORE?.urlPeriod || null;
    },
    selectedIndicatorId() {
      return this.getEntityId(this.selectedIndicator);
    },
    selectedDatasourceId() {
      return this.getEntityId(this.selectedDatasource);
    },
    indicatorTitle() {
      return this.readField(this.indicatorDetails || this.selectedIndicator, [
        'full_name',
        'short_name',
        'name',
      ]) || 'Current indicator';
    },
    indicatorRows() {
      const source = this.indicatorDetails || this.selectedIndicator || {};
      return this.cleanRows([
        ['Short name', this.readField(source, ['short_name', 'name'])],
        ['Full name', this.readField(source, ['full_name'])],
        ['Program area', this.readField(source, ['program_area'])],
        ['Type', this.readField(source, ['indicator_type', 'type'])],
        ['Current period', this.selectedPeriod],
        ['Description', this.readField(source, ['description', 'definition'])],
      ]);
    },
    datasourceRows() {
      const source = this.datasourceDetails || this.selectedDatasource || {};
      return this.cleanRows([
        ['Name', this.readField(source, ['datasource', 'name', 'short_name'])],
        ['Classification', this.readField(source, ['classification'])],
        ['Methodology', this.readField(source, ['methodology'])],
        ['Year available', this.readField(source, ['year_available'])],
        ['Subnational data', this.readField(source, ['subnational_data'])],
        ['Link', this.readField(source, ['link'])],
      ]);
    },
    visibleIndicatorDatasources() {
      return this.indicatorDatasources.slice(0, 8);
    },
    visibleTags() {
      if (!this.selectedIndicatorId) return [];
      return this.tags
        .filter((tag) => {
          const indicators = tag.indicators || tag.indicator || [];
          if (Array.isArray(indicators)) {
            return indicators.some((item) => this.getEntityId(item) === this.selectedIndicatorId);
          }
          return this.getEntityId(indicators) === this.selectedIndicatorId;
        })
        .slice(0, 8);
    },
  },
  watch: {
    selectedIndicatorId: {
      immediate: true,
      handler() {
        this.loadMetadata();
      },
    },
    selectedDatasourceId() {
      this.loadMetadata();
    },
  },
  methods: {
    setActiveComponent(component) {
      this.$emit('setActiveComponent', component);
    },
    getEntityId(value) {
      if (value == null) return null;
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
      }
      if (typeof value === 'object') {
        return this.getEntityId(value.id || value.value || value.indicator || value.datasource);
      }
      return null;
    },
    findById(collection, id) {
      if (!Array.isArray(collection) || id == null) return null;
      return collection.find((item) => this.getEntityId(item) === id) || null;
    },
    readField(source, fields) {
      if (!source || typeof source !== 'object') return '';
      const field = fields.find((key) => source[key] !== undefined && source[key] !== null);
      return field ? source[field] : '';
    },
    formatValue(value) {
      if (value === undefined || value === null || value === '') return EMPTY_VALUE;
      if (Array.isArray(value)) return value.length ? value.join(', ') : EMPTY_VALUE;
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'object') {
        return value.name || value.title || value.full_name || value.datasource || EMPTY_VALUE;
      }
      return String(value);
    },
    cleanRows(rows) {
      return rows.map(([label, value]) => ({
        label,
        value: this.formatValue(value),
      }));
    },
    async loadMetadata() {
      if (!this.selectedIndicatorId && !this.selectedDatasourceId) {
        this.error = 'Select an indicator or datasource to view metadata.';
        return;
      }

      this.loading = true;
      this.error = '';
      this.indicatorDetails = this.selectedIndicator;
      this.datasourceDetails = this.selectedDatasource;
      this.availableYears = [];
      this.indicatorDatasources = [];

      const requests = [];

      if (this.selectedIndicatorId) {
        requests.push(
          ApiServices.getSingleIndicatorObj(this.selectedIndicatorId)
            .then((response) => {
              this.indicatorDetails = response?.data || this.indicatorDetails;
            }),
        );
        requests.push(
          ApiServices.getIndicatorsWithAvailable(this.selectedIndicatorId)
            .then((response) => {
              this.availableYears = this.extractYears(response?.data);
            }),
        );
        requests.push(
          ApiServices.getIndicatorDatasources(this.selectedIndicatorId)
            .then((response) => {
              this.indicatorDatasources = this.extractResults(response?.data);
            }),
        );
      }

      if (this.selectedDatasourceId) {
        requests.push(
          ApiServices.getSingleDataSourceObj(this.selectedDatasourceId)
            .then((response) => {
              this.datasourceDetails = response?.data || this.datasourceDetails;
            }),
        );
      }

      requests.push(
        ApiServices.getTags()
          .then((response) => {
            this.tags = this.extractResults(response?.data);
          }),
      );

      const results = await Promise.all(
        requests.map((request) => request
          .then(() => ({ ok: true }))
          .catch(() => ({ ok: false }))),
      );
      const failed = results.filter((result) => !result.ok);

      if (failed.length === results.length) {
        this.error = 'Metadata could not be loaded right now.';
      }

      this.loading = false;
    },
    extractResults(data) {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.results)) return data.results;
      if (Array.isArray(data?.datasources)) return data.datasources;
      return [];
    },
    extractYears(data) {
      const values = this.extractResults(data);
      const source = values.length ? values : data?.['years_available'] || data?.years || [];
      if (!Array.isArray(source)) return [];

      return [...new Set(source.map((item) => (
        typeof item === 'object' ? item.period || item.year || item.name : item
      )).filter(Boolean))].slice(0, 12);
    },
  },
};
</script>

<style scoped>
.metadata-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: min(430px, 100vw);
  height: 100vh;
  background: #ffffff;
  color: #1c2520;
  border-left: 1px solid #d7e5de;
  box-shadow: -18px 0 36px rgba(12, 35, 26, 0.18);
  z-index: 10000;
  display: flex;
  flex-direction: column;
}

.metadata-drawer__header {
  min-height: 86px;
  padding: 18px 20px;
  background: #f4faf7;
  border-bottom: 1px solid #dcebe4;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.metadata-drawer__eyebrow {
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  color: #007d53;
  text-transform: uppercase;
}

.metadata-drawer__header h2 {
  margin: 0;
  color: #17251f;
  font-size: 18px;
  line-height: 1.35;
  font-weight: 700;
}

.metadata-drawer__close {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border: 1px solid #cfe2d9;
  border-radius: 50%;
  color: #314b40;
  background: #fff;
  cursor: pointer;
}

.metadata-drawer__body {
  overflow-y: auto;
  padding: 18px 20px 28px;
}

.metadata-drawer__status,
.metadata-drawer__notice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px;
  border: 1px solid #dcebe4;
  background: #f8fcfa;
  color: #325346;
  border-radius: 6px;
  font-size: 13px;
}

.metadata-section {
  padding: 0 0 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #edf2ef;
}

.metadata-section__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #007d53;
  font-size: 13px;
  font-weight: 700;
}

.metadata-list {
  display: grid;
  grid-template-columns: 110px 1fr;
  column-gap: 14px;
  row-gap: 10px;
  margin: 0;
}

.metadata-list dt {
  color: #6a7a73;
  font-size: 12px;
  font-weight: 700;
}

.metadata-list dd {
  margin: 0;
  color: #1f3029;
  font-size: 13px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.metadata-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.metadata-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #e8f5ef;
  color: #006745;
  font-size: 12px;
  font-weight: 700;
}

.metadata-source-list {
  margin: 0;
  padding-left: 18px;
  color: #1f3029;
  font-size: 13px;
  line-height: 1.6;
}

.metadata-empty {
  margin: 0;
  color: #73847d;
  font-size: 13px;
}

@media (max-width: 640px) {
  .metadata-drawer {
    width: 100vw;
  }

  .metadata-list {
    grid-template-columns: 1fr;
    row-gap: 4px;
  }
}
</style>
